import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Box, Text } from "@chakra-ui/react";

import AirBundlePayoutActions from "components/Journey/AirBundle/AirBundlePayoutActions";
import PayoutForm from "components/Journey/AirBundle/Payment/PayoutForm";
import UserCards from "components/Journey/AirBundle/UserCards";
import { Card } from "elements";
import useUpdateAirLuggageForJourney from "hooks/api/airBundle/useUpdateAirLuggageForJourneys";
import { getMyProfile } from "hooks/api/userProfiles/useFetchMyProfile/useFetchMyProfile";
import { AirLuggageJourney } from "models/journeys/AirLuggageJourney";

import getAirLuggageStatusBadge from "../getAirLuggageStatusBadge";
import { IBankDetailsForm } from "../PayoutPaymentView/PayoutPaymentView";

interface IComponent {
  journeyListData: AirLuggageJourney[];
  onPayoutSuccessful: (successful: boolean) => void;
  journeyId: number;
}

const PayoutUpdateView: React.FC<IComponent> = ({ journeyListData, onPayoutSuccessful, journeyId }) => {
  const form = useForm({ mode: "onSubmit" });
  const { t } = useTranslation();
  const userProfile = getMyProfile();

  const [submitError, setSubmitError] = useState<boolean>(false);
  const [isSubmitComplete, setIsSubmitComplete] = useState(false);

  const updatePayout = useUpdateAirLuggageForJourney();
  const status = journeyListData[0]?.status;
  const beneficiaryFullName = userProfile?.firstName + " " + userProfile?.lastName;

  useEffect(() => {
    onPayoutSuccessful(isSubmitComplete && !submitError);
  }, [isSubmitComplete, submitError]);

  const onSubmit = async (data: IBankDetailsForm) => {
    setSubmitError(false);

    const params = {
      countryId: data.country.value as string,
      bankFields: { ...data, countryOfResidence: data?.residence?.value },
    };

    updatePayout.mutate(
      { journeyId: journeyId, params },
      {
        onError: () => {
          setSubmitError(true);
        },
        onSuccess: () => {
          setIsSubmitComplete(true);
        },
      }
    );
  };

  const statusBox = getAirLuggageStatusBadge(status);

  return (
    <>
      <Box backgroundColor="greyscale.300" borderRadius="xl" padding={{ base: 4, lg: 8 }}>
        <Text fontSize="lg" fontWeight="medium" mb={4} data-testid="air-luggage-payout-luggage-details-title">
          {t("instant_cash.payout.bank_details")}
        </Text>
        <Card padding={0} border="1px solid" borderColor="primary.500" overflow="visible">
          <UserCards airBundleJourneys={journeyListData} statusBox={statusBox} />
          <PayoutForm
            form={form}
            onSubmit={onSubmit}
            submitError={submitError}
            isAirLuggage
            beneficiaryFullName={beneficiaryFullName}
            journeyId={journeyId}
          />
        </Card>
      </Box>
      <AirBundlePayoutActions form={form} onSubmit={onSubmit} submitError={submitError} isAirLuggage />
    </>
  );
};

export default PayoutUpdateView;
